/**
 * Runtime scene manifest types + loaders.
 *
 * Scene-specific data (asset URLs, spawn, hotspots, zones, portals, objects) lives in
 * per-scene `scene.json` files under public/assets/scenes/<id>/, fetched at
 * runtime so scenes can be added/edited without recompiling. A top-level
 * `registry.json` lists all scenes and the entry scene.
 */

import type { SceneObject } from '../scene/SceneObjectTypes';

export interface PanelInfo {
    title: string;
    content: string;
}

export interface HotspotConfig {
    id: string;
    name: string;
    /** World-space position [x, y, z]. */
    position: [number, number, number];
    /** Trigger radius (meters). */
    radius: number;
    type: 'equipment' | 'safety' | 'info';
    panel: PanelInfo;
    /** Marker color (rgb 0..1). */
    color?: [number, number, number];
}

export interface TriggerZoneConfig {
    id: string;
    name: string;
    type: 'box';
    /** World-space center [x, y, z]. */
    center: [number, number, number];
    /** Full size [x, y, z]. */
    size: [number, number, number];
    onEnter?: string;
    panel?: PanelInfo;
}

export interface PortalConfig {
    id: string;
    name: string;
    /** Portal volume center [x, y, z]. */
    position: [number, number, number];
    /** Portal box size [x, y, z]. */
    size: [number, number, number];
    /** Target scene id in the registry. */
    targetScene: string;
    targetRoomId?: string;
    /** Spawn position in the target scene. */
    targetSpawn: [number, number, number];
    /** Spawn angles in the target scene [pitch, yaw]. */
    targetAngles?: [number, number];
    /** Portal marker color (rgb 0..1). */
    color?: [number, number, number];
}

export interface SceneCubemapFaces {
    px: string;
    nx: string;
    py: string;
    ny: string;
    pz: string;
    nz: string;
}

export type SceneSkyboxConfig = {
    type: 'texture';
    /** Runtime URL of a PlayCanvas-compatible cubemap / skybox texture. */
    url: string;
    /** Visual multiplier for the skybox. Defaults to 1. */
    intensity?: number;
    /** Cubemap yaw rotation in degrees. Runtime supports any value from 0 to 360. */
    rotationYaw?: number;
} | {
    type: 'cubemap';
    /** Runtime URLs for PlayCanvas cubemap faces: +X, -X, +Y, -Y, +Z, -Z. */
    faces: SceneCubemapFaces;
    /** Visual multiplier for the skybox. Defaults to 1. */
    intensity?: number;
    /** Cubemap yaw rotation in degrees. Runtime supports any value from 0 to 360. */
    rotationYaw?: number;
} | {
    type: 'video';
    /** Runtime URL of a panoramic (equirect 360°) video. */
    url: string;
    /** Whether the video should auto-play. Defaults to true. */
    autoplay?: boolean;
    /** Whether the video should loop. Defaults to true. */
    loop?: boolean;
    /** Visual multiplier for the skybox. Defaults to 1. */
    intensity?: number;
    /** Cubemap yaw rotation in degrees. Runtime supports any value from 0 to 360. */
    rotationYaw?: number;
};

export interface SceneManifest {
    id: string;
    name: string;
    /** Splat asset URL (lod-meta.json for streamed LOD, or scene.sog). */
    splatUrl: string;
    /** Optional reduced splat asset URL for mobile devices. */
    mobileSplatUrl?: string;
    /**
     * Heavy scene marker for mobile: when true, phones unconditionally run the
     * degrade tier (600k splat budget / LOD cap 3 / pixel ratio 1.0 — see
     * stabilityGuard) on EVERY entry, before any crash can happen. Use for
     * scenes shipped with a desktop-scale LOD tree and no mobileSplatUrl asset
     * (e.g. meihuashan, 784MB). Remove once a mobile-decimated asset exists.
     */
    mobileLowSpec?: boolean;
    /** Collision asset URL (.glb recommended, or .voxel.json). */
    collisionUrl: string;
    /**
     * Occlusion mesh URL (GLB). When omitted, defaults to `collisionUrl` at
     * load time — the collision proxy doubles as an invisible depth occluder so
     * the third-person character is hidden behind walls. Rendered with
     * colorWrite=false / depthWrite=true on the Occlusion layer.
     */
    occlusionUrl?: string;
    /** Optional per-scene skybox. */
    skybox?: SceneSkyboxConfig;
    /** Optional lower-cost skybox used on mobile devices. Falls back to skybox. */
    mobileSkybox?: SceneSkyboxConfig;
    /** Initial player position [x, y, z]. */
    spawn: [number, number, number];
    /** Initial camera angles [pitch, yaw] in degrees. */
    spawnAngles: [number, number];
    /** Preferred controller when collision is available. */
    defaultMode: 'fly' | 'walk';
    /** Y below which the walk controller resets to spawn. */
    fallLimit: number;
    hotspots: HotspotConfig[];
    triggerZones: TriggerZoneConfig[];
    portals: PortalConfig[];
    /** Unified Scene Builder objects (preferred). Legacy arrays are converted
     *  to objects on load when this is absent (see convertLegacyToObjects). */
    objects?: SceneObject[];
}

export interface ScenesRegistry {
    /** Entry scene id loaded on startup. */
    entry: string;
    scenes: { id: string; name: string }[];
}

/** Fetch and parse the scene registry. */
export async function fetchRegistry(url: string): Promise<ScenesRegistry> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`无法加载场景注册表: ${url} (${res.status})`);
    return (await res.json()) as ScenesRegistry;
}

/** Fetch and parse a scene manifest. */
export async function fetchSceneManifest(url: string): Promise<SceneManifest> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`无法加载场景清单: ${url} (${res.status})`);
    return (await res.json()) as SceneManifest;
}

/**
 * Resolve the runtime object list for a manifest: use `objects` if present,
 * otherwise convert the legacy `hotspots`/`triggerZones`/`portals` arrays into
 * SceneObjects. This lets the runtime (SceneObjectManager) deal with a single
 * shape while old scene.json files keep working unchanged.
 */
export function convertLegacyToObjects(manifest: SceneManifest): SceneObject[] {
    // Prefer the unified `objects` list when explicitly provided. A missing
    // (undefined) `objects` falls back to converting the legacy arrays, so old
    // scene.json files keep working without edits.
    if (manifest.objects) {
        // De-duplicate ids: if the same id appears multiple times (older scene
        // files may have this), rename subsequent ones to avoid selection bugs.
        const seen = new Set<string>();
        let dupCounter = 0;
        for (const o of manifest.objects) {
            if (seen.has(o.id)) {
                o.id = `${o.id}-dup${++dupCounter}`;
            }
            seen.add(o.id);
        }
        return manifest.objects;
    }

    const objects: SceneObject[] = [];
    for (const h of manifest.hotspots ?? []) {
        objects.push({
            id: h.id,
            type: 'hotspot',
            name: h.name,
            transform: { position: h.position, rotation: [0, 0, 0], scale: [1, 1, 1] },
            marker: { kind: 'primitive', shape: 'sphere', color: h.color, scale: [0.24, 0.24, 0.24] },
            interaction: {
                mode: 'pressE',
                radius: h.radius,
                events: [{
                    id: `${h.id}-panel`,
                    type: 'showPanel',
                    name: h.panel.title,
                    payload: { title: h.panel.title, content: h.panel.content }
                }]
            }
        });
    }
    for (const z of manifest.triggerZones ?? []) {
        objects.push({
            id: z.id,
            type: 'triggerZone',
            name: z.name,
            transform: { position: z.center, rotation: [0, 0, 0], scale: [1, 1, 1] },
            marker: { kind: 'primitive', shape: 'box', scale: z.size, opacity: 0.15 },
            trigger: { size: z.size },
            interaction: z.panel ? {
                mode: 'enter',
                events: [{
                    id: `${z.id}-panel`,
                    type: 'showPanel',
                    name: z.panel.title,
                    payload: { title: z.panel.title, content: z.panel.content }
                }]
            } : { mode: 'enter', events: [] }
        });
    }
    for (const p of manifest.portals ?? []) {
        objects.push({
            id: p.id,
            type: 'portal',
            name: p.name,
            transform: { position: p.position, rotation: [0, 0, 0], scale: [1, 1, 1] },
            marker: { kind: 'primitive', shape: 'box', color: p.color, scale: p.size, opacity: 0.35 },
            interaction: { mode: 'enter', events: [] },
            portal: {
                targetRoomId: p.targetRoomId, targetScene: p.targetScene,
                targetSpawn: p.targetSpawn,
                targetAngles: [p.targetAngles?.[0] ?? 0, p.targetAngles?.[1] ?? 0, 0]
            }
        });
    }
    return objects;
}
